// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HeaderClickable, { HeaderClickableStyle } from "@conklin/App/Components/Header/HeaderClickable";
import MiniCart from "@conklin/App/Components/MiniCart/MiniCart";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import IconCart from "@conklin/XDL/Icons/IconCart";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, xScreen } from "@conklin/XDL/Utilities";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";
import FloatingTooltip from "@conklin/App/Components/FloatingTooltip/FloatingTooltip";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    badge?: TypographyProps;
    headerClickable?: HeaderClickableStyle;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    badge: {
        size: 14,
        lineHeight: "17px",
        weight: fontConfig.weight.bold,
        transform: "capitalize",
        color: "common.ui1",
        css: css`
            background-color: ${color("primary")};
            height: 100%;
            min-width: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 2px;

            ${xScreen("lte", "md")} {
                height: 24px;
                min-width: 24px;
                border-radius: 2px;
            }
        `,
    },
};

const iconMap = {
    cart: IconCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    // XNG-Changes
    isTabletScreen,
}) => {
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const history = useHistory();

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    // if (isCompactHeaderCart) {
    //     return (
    //         <Link
    //             {...routerLink}
    //             color={theme.header.linkColor}
    //             href={cartUrl}
    //             icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
    //             className="myCarticon"
    //             data-test-selector="cartLink"
    //         >
    //             <Typography as="span" {...styles.compactCountText}>
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //             <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
    //         </Link>
    //     );
    // }

    const openCartPageHandler = () => {
        if (!cartUrl) {
            return;
        }
        history.push(cartUrl);
    };

    const headerClickable = (
        <HeaderClickable
            onClick={openCartPageHandler}
            data-test-selector="cartLink"
            title={translate("Cart")}
            {...(retainedTotalCountToDisplay === 0
                ? { iconProps: { src: IconCart } }
                : {
                      badge: (
                          <Typography
                              {...styles.badge}
                              className={`cartLines ${retainedTotalCountToDisplay === 0 && "cart-empty"}`}
                          >
                              {retainedTotalCountToDisplay !== undefined ? (
                                  retainedTotalCountToDisplay
                              ) : (
                                  <LoadingSpinner size={10} color="common.ui1" />
                              )}
                          </Typography>
                      ),
                  })}
            isOpen={isPopupShown}
            extendedStyles={styles.headerClickable}
        />
    );

    if (isTabletScreen) {
        return headerClickable;
    }

    return (
        <FloatingTooltip
            onOpenChange={open => setIsPopupShown(open)}
            offset={{ crossAxis: 0, mainAxis: 6 }}
            placement="bottom-end"
            shift={{ padding: 0 }}
            triggerComponent={
                <>
                    {headerClickable}
                    <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
                </>
            }
            enabled={!isTabletScreen}
            extendedStyles={styles.xngTooltip}
            overlay={true}
            showArrow={false}
        >
            <MiniCart isPopupShown={isPopupShown} />
        </FloatingTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
